import { useEffect, type RefObject } from 'react';

type UseLiksChatComposerMenuDismissOptions = {
  isAttachmentsMenuOpen: boolean;
  isKnowledgeMenuOpen: boolean;
  isImageMenuOpen: boolean;
  attachmentsMenuRef: RefObject<HTMLDivElement | null>;
  knowledgeMenuRef: RefObject<HTMLDivElement | null>;
  imageMenuRef: RefObject<HTMLDivElement | null>;
  setIsAttachmentsMenuOpen: (open: boolean) => void;
  setIsKnowledgeMenuOpen: (open: boolean) => void;
  setIsImageMenuOpen: (open: boolean) => void;
};

export function useLiksChatComposerMenuDismiss({
  isAttachmentsMenuOpen,
  isKnowledgeMenuOpen,
  isImageMenuOpen,
  attachmentsMenuRef,
  knowledgeMenuRef,
  imageMenuRef,
  setIsAttachmentsMenuOpen,
  setIsKnowledgeMenuOpen,
  setIsImageMenuOpen,
}: UseLiksChatComposerMenuDismissOptions): void {
  useEffect(() => {
    if (!isAttachmentsMenuOpen && !isKnowledgeMenuOpen && !isImageMenuOpen) {
      return;
    }

    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (
        isAttachmentsMenuOpen &&
        attachmentsMenuRef.current &&
        !attachmentsMenuRef.current.contains(target)
      ) {
        setIsAttachmentsMenuOpen(false);
      }

      if (
        isKnowledgeMenuOpen &&
        knowledgeMenuRef.current &&
        !knowledgeMenuRef.current.contains(target)
      ) {
        setIsKnowledgeMenuOpen(false);
      }

      if (
        isImageMenuOpen &&
        imageMenuRef.current &&
        !imageMenuRef.current.contains(target)
      ) {
        setIsImageMenuOpen(false);
      }
    };

    const handleDocumentKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsAttachmentsMenuOpen(false);
        setIsKnowledgeMenuOpen(false);
        setIsImageMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [
    attachmentsMenuRef,
    imageMenuRef,
    isAttachmentsMenuOpen,
    isImageMenuOpen,
    isKnowledgeMenuOpen,
    knowledgeMenuRef,
    setIsAttachmentsMenuOpen,
    setIsImageMenuOpen,
    setIsKnowledgeMenuOpen,
  ]);
}
